import React from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Underline from '@tiptap/extension-underline';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';

import { Box, IconButton, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import { useTheme } from '@mui/material/styles';

interface Props {
	value: string;
	onChange: (html: string) => void;
	placeholder?: string;
	textColor?: string;
	showToolbar?: boolean;
}

const RichTextEditor: React.FC<Props> = ({
	value,
	onChange,
	placeholder,
	textColor = '#000000', // Default text color white
	showToolbar = true,
}) => {
	const theme = useTheme();

	const editor = useEditor({
		extensions: [
			StarterKit,
			Bold,
			Italic,
			Underline,
			TextStyle,
			Color,
			BulletList,
			OrderedList,
			TextAlign.configure({
				types: ['heading', 'paragraph'],
				defaultAlignment: 'left'
			}),
			Placeholder.configure({ placeholder: placeholder || 'Description' })
		],
		content: value || '',
		onUpdate: ({ editor }: { editor: any }) => onChange(editor.getHTML()),
		editorProps: {
			attributes: {
				style: `color: ${textColor}; text-align: left;`
			}
		}
	});

	if (!editor) return null;

	const handleTextAlign = (alignment: 'left' | 'center' | 'right' | 'justify') =>
		editor.chain().focus().setTextAlign(alignment).run();

	const iconColor = { color: theme.palette.richTextEditor.icons.primary };

	return (
		<Box
			sx={{
				border: `1px solid ${theme.palette.text.subtle}`,
				borderRadius: '4px',
				padding: 1,
				backgroundColor: 'transparent',
				color: textColor,
				minHeight: 180
			}}
		>
			{/* Toolbar */}
			{showToolbar && (
			<Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
				<Tooltip title="Bold">
					<IconButton onClick={() => editor.chain().focus().toggleBold().run()}>
						<FormatBoldIcon sx={{ ...iconColor }} />
					</IconButton>
				</Tooltip>
				<Tooltip title="Italic">
					<IconButton onClick={() => editor.chain().focus().toggleItalic().run()}>
						<FormatItalicIcon sx={{ ...iconColor }} />
					</IconButton>
				</Tooltip>
				<Tooltip title="Underline">
					<IconButton onClick={() => editor.chain().focus().toggleUnderline().run()}>
						<FormatUnderlinedIcon sx={{ ...iconColor }} />
					</IconButton>
				</Tooltip>
				<Tooltip title="Bullet List">
					<IconButton onClick={() => editor.chain().focus().toggleBulletList().run()}>
						<FormatListBulletedIcon sx={{ ...iconColor }} />
					</IconButton>
				</Tooltip>
				<Tooltip title="Numbered List">
					<IconButton onClick={() => editor.chain().focus().toggleOrderedList().run()}>
						<FormatListNumberedIcon sx={{ ...iconColor }} />
					</IconButton>
				</Tooltip>

				{/* Alignment buttons */}
				<Tooltip title="Align Left">
					<IconButton onClick={() => handleTextAlign('left')}>
						<FormatAlignLeftIcon sx={{ ...iconColor }} />
					</IconButton>
				</Tooltip>

				<Tooltip title="Align Center">
					<IconButton onClick={() => handleTextAlign('center')}>
						<FormatAlignCenterIcon sx={{ ...iconColor }} />
					</IconButton>
				</Tooltip>

				<Tooltip title="Align Right">
					<IconButton onClick={() => handleTextAlign('right')}>
						<FormatAlignRightIcon sx={{ ...iconColor }} />
					</IconButton>
				</Tooltip>

				<Tooltip title="Justify">
					<IconButton onClick={() => handleTextAlign('justify')}>
						<FormatAlignJustifyIcon sx={{ ...iconColor }} />
					</IconButton>
				</Tooltip>

				{/* Color Picker */}
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<input
						type="color"
						defaultValue={textColor}
						onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
						title="Text Color"
						style={{
							cursor: 'pointer',
							width: 28,
							height: 28,
							border: 'none',
							background: 'transparent'
						}}
					/>
				</Box>
			</Box>
			)}

			{/* Editor Area */}
			<Box
				className="tiptap-editor"
				sx={{
					position: 'relative',
					backgroundColor: 'transparent',
					minHeight: 180,
					color: textColor,
					borderRadius: 4,
					padding: '0 8px',
					outline: 'none'
				}}
			>
				<EditorContent editor={editor} />
			</Box>

			<style>{`
                .ProseMirror {
                min-height: 10em;
                line-height: 1.5em;
                padding: 8px;
                margin: 0;
                }
                .ProseMirror p {
                margin: 0;
                }
                p.is-empty.is-editor-empty::before {
                content: attr(data-placeholder);
                color: ${theme.palette.inputNew.inputPlaceholder};
                position: absolute;
                pointer-events: none;
                user-select: none;
                top: 8px;
                z-index: 1;
                }
            `}</style>
		</Box>
	);
};

export default RichTextEditor;
